import incomeIcon from 'app/assets/icons/summary_icons/income.svg'
import { ContainerHeader } from 'app/components/display/text/standard_text/standard-headings/component'

import 'app/match/apply/application_summary_before_submit/components/manager_income_financials_container/style.scss'

const INCOME_COMPARISON_THRESHOLD = 0.15
const WEEKS_PER_MONTH = 4.33

interface VerifiedIncome {
  hasFinancialInsights: boolean
  verifiedMonthlyIncome?: number
  verifiedSavingsTotal?: number
  rentToIncomeRatio?: number
  financialInsightsDate?: string
}

interface Props {
  verifiedIncome?: VerifiedIncome | null
  selfReportedWeeklyIncome?: number | null
  displayedApplicant?: Record<string, unknown>
  onNewNoteUpdated?: () => void
  newNotesContent?: unknown
  onNoteIconClicked?: () => void
  onNewNoteAdded?: () => void
  onNoteCrossClicked?: () => void
  interpreteNoteResult?: Record<string, unknown>
  enableNotesButton?: boolean
  section?: string
  id?: string
  toggleRequestInfoModal?: () => void
  isManagerAdded?: boolean
}

function formatCurrency(value?: number | null): string {
  if (value == null) return '—'
  return `$${value.toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`
}

function formatPercentage(value?: number | null): string {
  if (value == null) return '—'
  return `${Math.round(value * 100)}%`
}

function getComparisonStatus(
  selfReportedWeekly?: number | null,
  verifiedMonthly?: number | null,
): 'differ' | 'match' | null {
  if (selfReportedWeekly == null || !verifiedMonthly) return null
  const selfReportedMonthly = selfReportedWeekly * WEEKS_PER_MONTH
  const diff = Math.abs(selfReportedMonthly - verifiedMonthly) / verifiedMonthly
  return diff > INCOME_COMPARISON_THRESHOLD ? 'differ' : 'match'
}

interface VerifiedMetricsProps {
  verifiedIncome: VerifiedIncome
  selfReportedWeeklyIncome?: number | null
  comparison: 'differ' | 'match' | null
}

function VerifiedMetrics({
  verifiedIncome,
  selfReportedWeeklyIncome,
  comparison,
}: VerifiedMetricsProps) {
  return (
    <div className="application-container-padding">
      <div className="verified-metrics">
        <div className="metric-row">
          <span className="income-category income-label">
            Verified monthly income
          </span>
          <span className="metric-value">
            {formatCurrency(verifiedIncome.verifiedMonthlyIncome)}
          </span>
        </div>
        {verifiedIncome.verifiedSavingsTotal != null && (
          <div className="metric-row">
            <span className="income-category income-label">Savings total</span>
            <span className="metric-value">
              {formatCurrency(verifiedIncome.verifiedSavingsTotal)}
            </span>
          </div>
        )}
        <div className="metric-row">
          <span className="income-category income-label">
            Rent-to-income ratio
          </span>
          <span className="metric-value">
            {formatPercentage(verifiedIncome.rentToIncomeRatio)}
          </span>
        </div>
        {verifiedIncome.financialInsightsDate && (
          <div className="metric-row">
            <span className="income-category income-label">Verified date</span>
            <span className="metric-value">
              {verifiedIncome.financialInsightsDate}
            </span>
          </div>
        )}
      </div>

      {comparison && (
        <div className="income-comparison">
          <span className="income-category income-label">
            Income comparison
          </span>
          <div className="comparison-rows">
            <div className="comparison-row">
              <span className="comparison-label">Self-reported:</span>
              <span className="comparison-value">
                {formatCurrency(
                  selfReportedWeeklyIncome != null
                    ? selfReportedWeeklyIncome * WEEKS_PER_MONTH
                    : null,
                )}
                /mo
              </span>
            </div>
            <div className="comparison-row">
              <span className="comparison-label">Verified:</span>
              <span className="comparison-value">
                {formatCurrency(verifiedIncome.verifiedMonthlyIncome)}/mo
              </span>
            </div>
            <div className={`comparison-indicator ${comparison}`}>
              {comparison === 'match'
                ? 'Income figures are consistent'
                : 'Income figures differ significantly'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const ManagerIncomeFinancialsContainer = ({
  verifiedIncome,
  selfReportedWeeklyIncome,
  displayedApplicant,
  onNewNoteUpdated,
  newNotesContent,
  onNoteIconClicked,
  onNewNoteAdded,
  onNoteCrossClicked,
  interpreteNoteResult = {},
  enableNotesButton,
  section = 'verifiedFinancials',
  id = '',
  toggleRequestInfoModal,
  isManagerAdded,
}: Props) => {
  if (!verifiedIncome || !verifiedIncome.hasFinancialInsights) return null

  const comparison = getComparisonStatus(
    selfReportedWeeklyIncome,
    verifiedIncome.verifiedMonthlyIncome,
  )

  return (
    <div
      id={id}
      className="container-component manager-income-financials-container bbs"
    >
      <ContainerHeader
        icon={incomeIcon}
        text="Verified Financial Insights"
        isEditable={false}
        displayedApplicant={displayedApplicant}
        onNewNoteUpdated={onNewNoteUpdated}
        newNotesContent={newNotesContent}
        onNoteIconClicked={onNoteIconClicked}
        onNewNoteAdded={onNewNoteAdded}
        onNoteCrossClicked={onNoteCrossClicked}
        interpreteNoteResult={interpreteNoteResult}
        enableNotesButton={enableNotesButton}
        section={section}
        toggleRequestInfoModal={toggleRequestInfoModal}
        isManagerAdded={isManagerAdded}
        toggleEditRenterApplication={undefined}
        targetGUID={undefined}
        iconHeaderCmp={undefined}
      />
      <VerifiedMetrics
        verifiedIncome={verifiedIncome}
        selfReportedWeeklyIncome={selfReportedWeeklyIncome ?? null}
        comparison={comparison}
      />
    </div>
  )
}

export default ManagerIncomeFinancialsContainer
